import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Carousel, Dropdown } from 'react-bootstrap';

import { UseOutletContextService } from './Service';
import { StateLanguage } from '../../../../languages/config/StateLanguage';
import { ServiceDA } from '../../../../services/admin/service.service';
import { Error } from '../../../../models/error.model';
import { Message } from '../../../../models/message.model';
import { GlobalSwal } from '../../../../styles/swal.style';
import { CompanyServices } from '../../../../models/company.services.model';
import { Service } from '../../../../models/service.model';
import { imageService } from '../../../../scripts/image.value.script';
import { datetimeFormat12h } from '../../../../scripts/date.time.format.script';
import { getError, getMessage } from '../../../../languages/translations/response';
import { sortColumn } from '../../../../scripts/table.script';
import { suffixLegend } from '../../../../scripts/name.legend.script';
import { errorWebHandler } from '../../../../scripts/error.web.handler.script';

import AppSortTable from '../../../../components/element/SortTable';
import AppPagination from '../../../../components/element/Pagination';
import AppPreloader from '../../../../components/Preloader';
import AppInventoryListHeader from '../component/InventoryListHeader';
import imgServiceDefault from '../../../../assets/themes/service.png';

export interface ServiceListPageProps {};

let errorResponse: Error, messageResponse: Message, servicesResponse: CompanyServices;

const ServiceListPage: React.FunctionComponent<ServiceListPageProps> = props => {
  const {setRoute, companyForUser, loadServicesForCompany} = UseOutletContextService()
  const {lang} = StateLanguage()
  const navigate = useNavigate()

  const [mounted, setMounted] = useState(false)
  const [filter, setFilter] = useState({search: '', options: {category: '', sort_field: '', sort_mode: ''}})
  const [checked, setChecked] = useState({count: 0, list: [] as string[]})
  const [pagination, setPagination] = useState({current: 1, total: 0, pages: 1, limit: 20})
  const [services, setServices] = useState<Service[] | null>(null)

  const loadServices = async (id_company: string, callback: any, page: number = 1, limit: number = pagination.limit) => {
    await ServiceDA.getServices(id_company, filter.search, filter.options.category, filter.options.sort_field, filter.options.sort_mode, page, limit).then( (response) => {
      if (response.status === 200) {
        servicesResponse = response.data?.data ?? response.data
        const serviceItems = servicesResponse.items ?? servicesResponse.services ?? []
        const pg = servicesResponse.pagination ?? { page, limit: pagination.limit, total: serviceItems.length, pages: 1 }

        setServices(serviceItems)
        setChecked({count: 0, list: []})
        setPagination({current: pg.page ?? page, total: pg.total ?? serviceItems.length, pages: pg.pages ?? 1, limit: pg.limit ?? pagination.limit})
        callback()
      } else {
        errorResponse = response.data

        GlobalSwal.fire({
          title: getError(errorResponse.code, lang.code, errorResponse.message),
          text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
          icon: 'error',
          confirmButtonText: lang.labels.okGotIt
        })
      }
    }).catch( (error) => {
      errorWebHandler(lang, error)
    })
  }

  const handleChangePage = (page: number, newLimit?: number) => {
    if (companyForUser) loadServices(companyForUser.id, () => {}, page, newLimit ?? pagination.limit)
  }

  const submitServiceDelete = (item: string) => {
    if (companyForUser) {
      GlobalSwal.fire({
        title: lang.labels.youWantDeleteService,
        text: lang.labels.youWillNotBeAbleToUndo,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: lang.labels.yesDelete,
        cancelButtonText: lang.labels.noCancel
      }).then(async (result) => {
        if (result.isConfirmed) {
          await ServiceDA.deleteServices(companyForUser.id, [item]).then( (response) => {
            if (response.status === 200) {
              messageResponse = response.data

              loadServices(companyForUser.id, () => {
                loadServicesForCompany(companyForUser.id)

                GlobalSwal.fire({
                  title: getMessage(messageResponse.message, lang.code),
                  text: lang.labels.actionCompletedReturningToPage,
                  icon: 'success',
                  showConfirmButton: false,
                  timer: 2200
                })
              })
            } else {
              errorResponse = response.data

              GlobalSwal.fire({
                title: getError(errorResponse.code, lang.code, errorResponse.message),
                text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
                icon: 'error',
                confirmButtonText: lang.labels.okGotIt
              })
            }
          }).catch( (error) => {
            errorWebHandler(lang, error)
          })
        }
      })
    }
  }

  const submitServiceUpdate = (item: string) => {
    navigate("/main/inventory/service/update/" + item)
  }

  const handleChangeSelectAllServices = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    let list: string[] = (event.target.checked) ? event.target.value.split(',') : []
    setChecked({count: list.length, list: list})
  }

  const handleChangeSelectService = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    let list: string[] = (event.target.checked) ? (!checked.list.includes(event.target.value)) ? checked.list.concat(event.target.value) : checked.list : checked.list.filter((item) => (item !== event.target.value))
    setChecked({count: list.length, list: list})
  }

  const executeSortList = (item: string) => {
    if (companyForUser) {
      sortColumn(item, filter.options)
      setFilter({search: filter.search, options: {...filter.options}})
      loadServices(companyForUser.id, () => {})
    }
  }

  useEffect( () => {
    if (companyForUser) {
      filter.search = ''
      filter.options.category = ''
      filter.options.sort_field = ''
      filter.options.sort_mode = ''
      setFilter({search: '', options: {category: '', sort_field: '', sort_mode: ''}})

      setServices(null)
      loadServices(companyForUser.id, () => {})
    }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyForUser])

  useEffect( () => {
    setMounted(true)
    setRoute({path: {root: lang.labels.services, branch: lang.labels.listServices}, company: true})

    return () => setMounted(false)

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!mounted) return null

  return (
    <div className="w-100 h-100">
      { services
        ?
        <div className="card card-flush">
          <AppInventoryListHeader inventory={"service"} filter={filter} setFilter={setFilter} checked={checked} loadInventory={loadServices}></AppInventoryListHeader>
          <div className="card-body pt-0">
            <div className="dataTables_wrapper">
              <div className="table-responsive">
                <table className="table align-middle table-row-dashed mb-2">
                  <thead className="border-bottom">
                    <tr className="text-start text-muted text-uppercase fw-bolder fs-7">
                      <th className="w-25px">
                        <div className="form-check form-check-sm form-check-custom form-check-solid">
                          <input className="form-check-input" type="checkbox" value={services.map(item => item.id)} checked={services.length === checked.count && checked.count > 0} onChange={handleChangeSelectAllServices} />
                        </div>
                      </th>
                      <th className="min-w-300px">
                        <div className="d-flex align-items-center text-nowrap cursor-pointer" onClick={() => executeSortList("service")}>
                          <span className="me-1">{lang.labels.service}</span>
                          <AppSortTable attribute={"service"} field={filter.options.sort_field} mode={filter.options.sort_mode}></AppSortTable>
                        </div>
                      </th>
                      <th className="min-w-150px">
                        <div className="d-flex align-items-center text-nowrap cursor-pointer" onClick={() => executeSortList("category")}>
                          <span className="me-1">{lang.labels.category}</span>
                          <AppSortTable attribute={"category"} field={filter.options.sort_field} mode={filter.options.sort_mode}></AppSortTable>
                        </div>
                      </th>
                      <th className="min-w-150px">
                        <div className="d-flex align-items-center text-nowrap cursor-pointer" onClick={() => executeSortList("tags")}>
                          <span className="me-1">{lang.labels.tags}</span>
                          <AppSortTable attribute={"tags"} field={filter.options.sort_field} mode={filter.options.sort_mode}></AppSortTable>
                        </div>
                      </th>
                      <th className="w-150px">
                        <div className="d-flex align-items-center text-nowrap cursor-pointer" onClick={() => executeSortList("last modified")}>
                          <span className="me-1">{lang.labels.lastModified}</span>
                          <AppSortTable attribute={"last modified"} field={filter.options.sort_field} mode={filter.options.sort_mode}></AppSortTable>
                        </div>
                      </th>
                      <th className="w-100px text-end">{lang.labels.actions}</th>
                    </tr>
                  </thead>
                  { services.length > 0
                    ?
                    <tbody className="text-secondary fw-bold">
                      { services.map (( (item, index) => { return (
                        <tr key={index} className={`row-dashed ${(index + 1) % 2 !== 0 ? 'odd' : 'even'}`}>
                          <td>
                            <div className="form-check form-check-sm form-check-custom form-check-solid">
                              <input className="form-check-input" type="checkbox" value={item.id} checked={checked.list.includes(item.id)} onChange={handleChangeSelectService} />
                            </div>
                          </td>
                          <td className="d-flex align-items-center">
                            <div className="me-5">
                              <Link to={"/main/inventory/service/view/" + item.id}>
                                { item.photos.length > 0
                                  ?
                                  <Carousel slide={false} controls={false} indicators={false} touch={false}>
                                    { item.photos.map (( (item, index) => { return (
                                      <Carousel.Item key={index}>
                                        <img src={imageService(item)} className="d-block w-50px h-50px" alt={`${lang.labels.photography}${suffixLegend(index)}`} />
                                      </Carousel.Item>
                                    )}))}
                                  </Carousel>
                                  :
                                  <div className="symbol symbol-50px overflow-hidden">
                                    <span className="symbol-label">
                                      <img src={imgServiceDefault} alt={lang.labels.photography} className="w-100 h-100" />
                                    </span>
                                  </div>
                                }
                              </Link>
                            </div>
                            <div className="d-flex flex-column">
                              <Link to={"/main/inventory/service/view/" + item.id} className="fw-bolder text-dark text-hover-primary mb-1">{item.display_name}</Link>
                              <small>{item.description}</small>
                            </div>
                          </td>
                          <td>{item.category?.display_name ?? ''}</td>
                          <td>
                            { (item.tags?.length ?? 0) > 0
                              ?
                              <>
                                { item.tags.map (( (sub_item, sub_index) => { return (
                                  <div key={sub_index} className="badge badge-light m-1">{sub_item}</div>
                                )}))}
                              </>
                              :
                              <small>{lang.labels.none}</small>
                            }
                          </td>
                          <td className="text-nowrap fs-7">{datetimeFormat12h(lang.code, item.updated_at)}</td>
                          <td className="text-end">
                            <Dropdown>
                              <Dropdown.Toggle variant="btn btn-sm btn-light btn-active-light-primary">
                                <div className="d-flex align-items-center">
                                  {lang.labels.actions}
                                  <span className="svg-icon svg-icon-5 me-0">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                      <path d="M11.4343 12.7344L7.25 8.55005C6.83579 8.13583 6.16421 8.13584 5.75 8.55005C5.33579 8.96426 5.33579 9.63583 5.75 10.05L11.2929 15.5929C11.6834 15.9835 12.3166 15.9835 12.7071 15.5929L18.25 10.05C18.6642 9.63584 18.6642 8.96426 18.25 8.55005C17.8358 8.13584 17.1642 8.13584 16.75 8.55005L12.5657 12.7344C12.2533 13.0468 11.7467 13.0468 11.4343 12.7344Z" fill="black"></path>
                                    </svg>
                                  </span>
                                </div>
                              </Dropdown.Toggle>
                              <Dropdown.Menu renderOnMount popperConfig={{ strategy: "fixed" }} variant="menu menu-sub menu-sub-dropdown menu-column menu-rounded menu-gray-600 menu-state-bg-light-primary w-auto p-3 fs-7" align="end">
                                <Dropdown.Item bsPrefix="menu-item" onClick={() => {submitServiceUpdate(item.id)} }>
                                  <p className="menu-link fw-bold px-3 py-1 m-0">{lang.labels.edit}</p>
                                </Dropdown.Item>
                                <Dropdown.Item bsPrefix="menu-item" onClick={() => submitServiceDelete(item.id)}>
                                  <p className="menu-link fw-bold px-3 py-1 m-0">{lang.labels.delete}</p>
                                </Dropdown.Item>
                              </Dropdown.Menu>
                            </Dropdown>
                          </td>
                        </tr>
                      )}))}
                    </tbody>
                    :
                    <tbody>
                      <tr className="text-secondary fw-bold">
                        <td colSpan={6} className="text-center">{lang.labels.noRecordsFound}</td>
                      </tr>
                    </tbody>
                  }
                </table>
              </div>
              <AppPagination
                showOption={true}
                pagination={{
                  current: pagination.current,
                  total: pagination.total,
                  limit: pagination.limit,
                  pages: Array.from({ length: pagination.pages }, (_, i) => i + 1),
                }}
                loadPagination={handleChangePage}
              ></AppPagination>
            </div>
          </div>
        </div>
        :
        <AppPreloader></AppPreloader>
      }
    </div>
  )
};

export default ServiceListPage;
